import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { FiMenu, FiSearch, FiX, FiDatabase, FiZap, FiChevronDown, FiCheck, FiLoader, FiAlertTriangle } from 'react-icons/fi'
import { WhatsAppInboxPanel } from './WhatsAppInboxPanel'
import { ConsultaDracoModal } from './ConsultaDracoModal'
import ConfirmDialog from './ConfirmDialog'
import { NEU as neu } from '../lib/neu'

// Ícone do Meta (mesmo SVG do DevicesPanel do crm) — padrão adotado pros menus
// de dispositivos da API oficial do Meta.
const MetaIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className}>
    <path d="M5,19.5c0-4.6,2.3-9.4,5-9.4c1.5,0,2.7,0.9,4.6,3.6c-1.8,2.8-2.9,4.5-2.9,4.5c-2.4,3.8-3.2,4.6-4.5,4.6C5.9,22.9,5,21.7,5,19.5 M20.7,17.8L19,15c-0.4-0.7-0.9-1.4-1.3-2c1.5-2.3,2.7-3.5,4.2-3.5c3,0,5.4,4.5,5.4,10.1c0,2.1-0.7,3.3-2.1,3.3S23.3,22,20.7,17.8 M16.4,11c-2.2-2.9-4.1-4-6.3-4C5.5,7,2,13.1,2,19.5c0,4,1.9,6.5,5.1,6.5c2.3,0,3.9-1.1,6.9-6.3c0,0,1.2-2.2,2.1-3.7c0.3,0.5,0.6,1,0.9,1.6l1.4,2.4c2.7,4.6,4.2,6.1,6.9,6.1c3.1,0,4.8-2.6,4.8-6.7C30,12.6,26.4,7,22.1,7C19.8,7,18,8.8,16.4,11"/>
  </svg>
)

// Painel de conversas WhatsApp do device "Faturamento · TWMS" — migrado do
// crm.twms.com.br (antes era só um link com handoff SSO). O componente do inbox
// (WhatsAppInboxPanel + ContactProfilePanel/ForwardModal/AssignLeaderModal) é
// cópia verbatim do crm; o backend continua TODO no crm-twms-backend, consumido
// pelos proxies finos do fin (routes/crm-inbox.cjs) e pelo /socket.io proxeado
// no nginx. Este wrapper faz o papel da "barra de dispositivos" do crm, com um
// device só.
export const FATURAMENTO_DEVICE_ID = 'meta_1078840475319778'
// Atendimento (24/09/2026): o número que manda o aviso do Prêmio de Incentivo.
// Só admin troca para ele aqui — é por onde as líderes respondem sobre o prêmio.
export const ATENDIMENTO_DEVICE_ID = 'meta_762195393654543'
// Incentivo - TWMS (06/10/2026): número próprio do Prêmio de Incentivo, em
// coexistência — o que se digita no celular aparece aqui (echoes gravados no crm).
export const INCENTIVO_DEVICE_ID = 'meta_594709588575744'
const DEVICES = [
  { id: FATURAMENTO_DEVICE_ID, nome: 'Faturamento', dica: 'Conversas do número de Faturamento (cobrança de boletos)' },
  { id: ATENDIMENTO_DEVICE_ID, nome: 'Atendimento', dica: 'Conversas do número de Atendimento' },
  { id: INCENTIVO_DEVICE_ID, nome: 'Incentivo', dica: 'Conversas do número Incentivo - TWMS (Prêmio de Incentivo), inclusive o que é digitado no celular' },
] as const

// Botão "Chamar" do painel Incentivos → abre esta tela no Atendimento, já na
// conversa da pessoa. O painel dispara o evento; o App guarda o pedido.
export const EVENTO_CHAMAR = 'fin-chamar-whatsapp'
// `lider` (25/09/2026): botão roxo "Falar com a líder" — abre a conversa com a LÍDER
// do grupo desta consultora (código/nome são os DELA; quem é a líder, o fin resolve).
export interface ChamarPedido { codigo: string; nome: string | null; lider?: boolean }

const capitalizar = (w: string) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '')
const primeiroNome = (n: string | null | undefined) => capitalizar(String(n || '').trim().split(/\s+/)[0] || '')
// "BEATRIZ DE FREITAS CORREA" → "Beatriz Correa": nome e último sobrenome, para a
// líder saber de QUAL consultora se fala sem a mensagem virar um cadastro.
const nomeCurto = (n: string | null | undefined) => {
  const ws = String(n || '').trim().split(/\s+/).filter(Boolean)
  if (ws.length <= 1) return capitalizar(ws[0] || '')
  return `${capitalizar(ws[0])} ${capitalizar(ws[ws.length - 1])}`
}

interface ConversasPanelProps {
  // Deep-link vindo da Cobrança ("Ver conversa"): auto-seleciona a conversa.
  openConversationId: string | null
  onOpenHandled: () => void
  // Pedido do botão "Chamar" (Incentivos): código Draco + nome.
  chamar?: ChamarPedido | null
  onChamarHandled?: () => void
  // O header global some nesta tela — o hambúrguer mobile vive aqui.
  onOpenMenu: () => void
}

export function ConversasPanel({ openConversationId, onOpenHandled, chamar, onChamarHandled, onOpenMenu }: ConversasPanelProps) {
  const { authenticatedFetch, user } = useAuth()
  // Qual número está na tela. Faturamento é o de sempre; Atendimento só para
  // admin (a troca some para quem não é).
  const [deviceId, setDeviceId] = useState<string>(FATURAMENTO_DEVICE_ID)
  const podeTrocar = !!user?.isAdmin
  const deviceNome = DEVICES.find(d => d.id === deviceId)?.nome || 'Faturamento'
  const ehFaturamento = deviceId === FATURAMENTO_DEVICE_ID
  // O contato que o botão "Chamar" pediu, já com o telefone resolvido.
  const [abrirContato, setAbrirContato] = useState<null | { phone: string; nome: string | null; convite: { template: string; variables: string[] } }>(null)
  const [chamarErro, setChamarErro] = useState<string | null>(null)

  useEffect(() => {
    if (!chamar?.codigo) return
    let cancelado = false
    setChamarErro(null)
    ;(async () => {
      try {
        if (chamar.lider) {
          const r = await authenticatedFetch(`/api/incentivos/contato-lider/${encodeURIComponent(chamar.codigo)}`)
          const d = await r.json().catch(() => ({}))
          if (cancelado) return
          if (!r.ok || !d?.success) throw new Error(d?.error || 'não consegui achar a líder dela')
          if (!d.lider) throw new Error(`${nomeCurto(chamar.nome) || 'Esta consultora'}: ${d.motivo || 'não achei a líder do grupo'}.`)
          if (!d.lider.para) throw new Error(`A líder ${nomeCurto(d.lider.nome)} não tem WhatsApp válido no app nem no cadastro.`)
          setDeviceId(d.deviceId || ATENDIMENTO_DEVICE_ID)
          setAbrirContato({
            phone: String(d.lider.para), nome: d.lider.nome,
            convite: { template: d.convite, variables: [primeiroNome(d.lider.nome) || 'tudo bem', nomeCurto(chamar.nome) || 'sua consultora'] },
          })
          return
        }
        const r = await authenticatedFetch(`/api/incentivos/contato/${encodeURIComponent(chamar.codigo)}`)
        const d = await r.json().catch(() => ({}))
        if (cancelado) return
        if (!r.ok || !d?.success) throw new Error(d?.error || 'não consegui achar o WhatsApp dela')
        if (!d.para) throw new Error(`${chamar.nome || 'Ela'} não tem WhatsApp válido no app nem no cadastro.`)
        setDeviceId(d.deviceId || ATENDIMENTO_DEVICE_ID)
        const primeiro = String(chamar.nome || '').trim().split(/\s+/)[0] || ''
        const nomeBonito = primeiro ? primeiro.charAt(0).toUpperCase() + primeiro.slice(1).toLowerCase() : ''
        setAbrirContato({ phone: String(d.para), nome: chamar.nome, convite: { template: d.convite, variables: [nomeBonito || 'tudo bem'] } })
      } catch (e: any) {
        if (!cancelado) setChamarErro(e?.message || 'não consegui abrir a conversa')
      } finally {
        if (!cancelado) onChamarHandled?.()
      }
    })()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chamar])
  const [search, setSearch] = useState('')
  // DOM node onde o inbox renderiza a toolbar (Lista×Kanban + campanha) via portal.
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null)
  const [displayPhone, setDisplayPhone] = useState<string | null>(null)
  // ── Atendimento automático (dropdown, pedido do diretor 20/08/2026) ──
  // Antes era um toggle ON/OFF cru. Virou um menu de 3 modos porque ligar "o
  // bot" sem dizer PRA QUEM era a parte perigosa: no modo campanha ele só fala
  // com quem está na lista de disparo, responde UMA vez com o valor atualizado
  // e o parcelamento, e para — a contraproposta é do humano.
  type BotMode = 'off' | 'campanha' | 'livre'
  interface Campanha { id: string; template_name: string; contacts_total: number; created_at: string; clicados: number }
  interface BotCfg {
    mode: BotMode; campaignId: string | null; maxParcelas: number; dryRun: boolean
    taxas: { multaPct: number; jurosDia: number } | null
    campanha: Campanha | null
  }
  const [botCfg, setBotCfg] = useState<BotCfg | null>(null)
  const [botSaving, setBotSaving] = useState(false)
  const [botMenuOpen, setBotMenuOpen] = useState(false)
  const botMenuRef = useRef<HTMLDivElement | null>(null)
  const [campanhas, setCampanhas] = useState<Campanha[]>([])
  const [backlog, setBacklog] = useState<{ total: number; dentroJanela: number; foraJanela: number } | null>(null)
  const [backlogJob, setBacklogJob] = useState<any>(null)
  const [confirmBacklog, setConfirmBacklog] = useState(false)
  // Erro aparece DENTRO do menu, não em alert() do navegador.
  const [botErro, setBotErro] = useState<string | null>(null)
  const botMode: BotMode = botCfg?.mode || 'off'
  // Consulta Draco — busca financeira de qualquer cliente no Oracle (caso
  // Eliane Potrich: "consta pago fulana?" sem sair do Conversas).
  const [showConsultaDraco, setShowConsultaDraco] = useState(false)

  useEffect(() => {
    authenticatedFetch('/api/whatsapp-devices')
      .then(r => r.json())
      .then((devices: any[]) => {
        const dev = (Array.isArray(devices) ? devices : []).find(d => d.id === deviceId)
        setDisplayPhone(dev?.display_phone || null)
      })
      .catch(() => {})
  }, [authenticatedFetch, deviceId])

  useEffect(() => {
    authenticatedFetch('/api/cobranca-bot/config')
      .then(r => r.json())
      .then(d => { if (d?.success) setBotCfg(d) })
      .catch(() => {})
  }, [authenticatedFetch])

  // Fecha ao clicar fora — mesmo padrão do dropdown de campanha do inbox
  // (não existe componente de dropdown reusável neste projeto).
  useEffect(() => {
    if (!botMenuOpen) return
    const onDown = (e: MouseEvent) => {
      if (botMenuRef.current && !botMenuRef.current.contains(e.target as Node)) setBotMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [botMenuOpen])

  // Campanhas e backlog custam query pesada — só ao ABRIR o menu, não a cada
  // montagem do painel.
  useEffect(() => {
    if (!botMenuOpen) return
    authenticatedFetch('/api/cobranca-bot/campaigns')
      .then(r => r.json()).then(d => { if (d?.success) setCampanhas(d.campanhas || []) }).catch(() => {})
    authenticatedFetch('/api/cobranca-bot/backlog')
      .then(r => r.json()).then(d => { if (d?.success) setBacklog(d) }).catch(() => {})
  }, [botMenuOpen, authenticatedFetch])

  // Grava modo + campanha numa chamada só: dois PATCH separados poderiam deixar
  // o device ligado apontando pra campanha nenhuma.
  const salvarBot = async (patch: { mode?: BotMode; campaignId?: string | null }) => {
    const alvo: BotMode = patch.mode ?? botMode
    setBotErro(null)
    setBotSaving(true)
    try {
      const r = await authenticatedFetch('/api/cobranca-bot/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: alvo, campaignId: patch.campaignId ?? botCfg?.campaignId ?? null }),
      })
      const d = await r.json()
      if (r.ok && d?.success) setBotCfg(d)
      else setBotErro(d?.error || 'não consegui salvar')
    } catch { setBotErro('não consegui salvar — o servidor não respondeu') }
    finally { setBotSaving(false) }
  }

  // Detalhes do diálogo de confirmação — o disparo é irreversível, então a
  // tela diz exatamente quantas saem, quantas ficam de fora e por quê.
  const backlogDetalhes = () => [
    `${backlog?.dentroJanela ?? 0} conversa(s) recebem a oferta agora`,
    ...(backlog?.foraJanela ? [`${backlog.foraJanela} ficam de fora — passaram da janela de 24h da Meta e só aceitam template aprovado`] : []),
    'Conversa que já tem resposta sua NÃO é tocada',
    'Sai uma a cada ~4 segundos; dá pra acompanhar o progresso aqui no menu',
    ...(botCfg?.dryRun ? ['MODO DE TESTE ligado: nada é enviado de verdade, só vai pro log'] : []),
  ]

  const responderBacklog = async () => {
    setConfirmBacklog(false)
    setBotErro(null)
    try {
      const r = await authenticatedFetch('/api/cobranca-bot/backlog/run', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      })
      const d = await r.json()
      if (!r.ok || !d?.success) { setBotErro(d?.error || 'não consegui iniciar o envio'); return }
      const timer = setInterval(async () => {
        try {
          const s = await (await authenticatedFetch('/api/cobranca-bot/backlog/status')).json()
          setBacklogJob(s?.job || null)
          if (s?.job && !s.job.rodando) {
            clearInterval(timer)
            authenticatedFetch('/api/cobranca-bot/backlog')
              .then(x => x.json()).then(x => { if (x?.success) setBacklog(x) }).catch(() => {})
          }
        } catch { clearInterval(timer) }
      }, 2000)
    } catch { setBotErro('não consegui iniciar o envio — o servidor não respondeu') }
  }

  return (
    // Preenche o box global do App (respiro + cartão + sombra + scroll já vêm de lá).
    <div className={`h-full flex flex-col overflow-hidden${neu ? ' neu-lab' : ''}`}>
      {/* ── Barra do device (versão de um device só da barra do crm) ── */}
      <div className={`flex items-center gap-2 px-2 border-b border-border bg-card flex-shrink-0 ${neu ? 'neu-bar pt-2.5 pb-1.5' : 'py-1.5'}`}>
        <button
          onClick={onOpenMenu}
          title="Menu"
          aria-label="Abrir menu"
          className="lg:hidden flex items-center justify-center w-8 h-8 shrink-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary"
        >
          <FiMenu className="w-5 h-5" />
        </button>
        {/* Slot da toolbar do inbox — preenchido via portal pelo WhatsAppInboxPanel */}
        <div ref={setToolbarSlot} className="flex items-center gap-1.5 min-w-0" />

        {/* Admin troca de número (24/09): Faturamento é a cobrança, Atendimento
            o geral, Incentivo (06/10) o do Prêmio de Incentivo. */}
        {podeTrocar && (
          <div className={`inline-flex items-center gap-0.5 p-0.5 rounded-lg shrink-0 ${neu ? 'order-2 neu-inset p-1 rounded-xl' : 'border border-foreground/20 bg-foreground/5'}`}
            role="tablist" aria-label="Número do WhatsApp">
            {DEVICES.map(d => (
              <button key={d.id} role="tab" aria-selected={deviceId === d.id}
                onClick={() => { setDeviceId(d.id); setAbrirContato(null) }}
                title={d.dica}
                className={`inline-flex items-center gap-1.5 h-7 sm:h-8 px-2.5 rounded-md text-xs font-medium transition-colors ${
                  neu
                    ? (deviceId === d.id ? 'neu-seg-btn neu-on text-foreground' : 'neu-seg-btn text-muted-foreground hover:text-foreground')
                    : (deviceId === d.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}`}>
                <MetaIcon className="w-3.5 h-3.5 shrink-0" />
                <span>{d.nome}</span>
              </button>
            ))}
          </div>
        )}

        {/* No tema neu o chip do device fica OCULTO: com um único número, o
            rótulo só ocupa espaço — mesma decisão do crm. */}
        {!neu && !podeTrocar && (
        <div
          className="inline-flex items-center gap-1.5 h-8 sm:h-9 px-2.5 rounded-lg border border-foreground/20 bg-foreground/5 text-xs font-medium text-foreground"
          title={`Conversas WhatsApp do número de Faturamento (cobrança de boletos)${displayPhone ? ` · ${displayPhone}` : ''}`}
        >
          <MetaIcon className="w-4 h-4 shrink-0" />
          <span className="truncate">Faturamento · TWMS</span>
        </div>
        )}

        {/* ── Atendimento automático: dropdown de 3 modos ──
            'campanha' é o modo seguro: só fala com quem está na lista de disparo,
            manda UMA resposta com o valor atualizado + parcelamento e para ali.
            'livre' é o bot de IA que negocia a conversa inteira. */}
        {ehFaturamento && (
        <div ref={botMenuRef} className={`relative shrink-0${neu ? ' order-2' : ''}`}>
          <button
            onClick={() => setBotMenuOpen(o => !o)}
            disabled={botSaving}
            title={
              botMode === 'campanha'
                ? `Régua da campanha LIGADA${botCfg?.campanha ? ` (${botCfg.campanha.template_name})` : ''} — responde uma vez quem clicou na campanha, com o valor atualizado e o parcelamento, e para. Quem você já respondeu não é tocado.`
                : botMode === 'livre'
                  ? 'Bot livre LIGADO — a IA negocia a conversa inteira seguindo a régua de cobrança. Ao você assumir uma conversa, o bot pausa nela sozinho.'
                  : 'Atendimento automático DESLIGADO — tudo manual. Clique para escolher um modo.'
            }
            className={neu
              ? `relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 neu-relief ${
                  botMode === 'campanha' ? 'bg-sky-500/15 text-sky-600' : botMode === 'livre' ? 'bg-green-500/15 text-green-600' : 'text-muted-foreground'
                }`
              : `inline-flex items-center gap-1.5 h-8 sm:h-9 px-2.5 max-w-[220px] rounded-lg border text-xs font-medium transition-colors disabled:opacity-50 ${
                  botMode === 'campanha'
                    ? 'bg-sky-500/15 border-sky-500/40 text-sky-700 dark:text-sky-300'
                    : botMode === 'livre'
                      ? 'bg-green-500/15 border-green-500/40 text-green-600'
                      : 'bg-foreground/5 border-foreground/20 text-muted-foreground hover:bg-foreground/10'
                }`}
          >
            {neu ? (
              <>
                <FiZap className="w-3.5 h-3.5" />
                <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${
                  botMode === 'campanha' ? 'bg-sky-500' : botMode === 'livre' ? 'bg-green-500' : 'bg-muted-foreground/40'
                }`} />
              </>
            ) : (
              <>
                <FiZap className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">
                  {botMode === 'campanha'
                    ? (botCfg?.campanha?.template_name || 'Régua da campanha')
                    : botMode === 'livre' ? 'Bot livre' : 'Automático OFF'}
                </span>
                {botSaving
                  ? <FiLoader className="w-3.5 h-3.5 shrink-0 animate-spin" />
                  : <FiChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${botMenuOpen ? 'rotate-180' : ''}`} />}
              </>
            )}
          </button>

          {botMenuOpen && (
            <div className="absolute right-0 top-full mt-1.5 z-50 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg">
              <div className="px-3 py-2 border-b border-border/50 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Atendimento automático
              </div>

              {([
                ['off', 'Desligado', 'Atendimento 100% manual.'],
                ['campanha', 'Régua da campanha', 'Responde 1× quem está na lista da campanha, com o valor atualizado e o parcelamento. Depois para e o humano assume.'],
                ['livre', 'Bot livre (IA negocia)', 'A IA conduz a conversa inteira. Mais alcance, menos controle.'],
              ] as [BotMode, string, string][]).map(([m, titulo, desc]) => (
                <button key={m} onClick={() => { salvarBot({ mode: m }); if (m !== 'campanha') setBotMenuOpen(false) }}
                  disabled={botSaving || (m === 'campanha' && !botCfg?.campaignId && campanhas.length === 0)}
                  className="w-full text-left px-3 py-2 hover:bg-secondary disabled:opacity-40 flex gap-2">
                  <span className="w-3 shrink-0 pt-0.5">{botMode === m && <FiCheck className="w-3 h-3 text-sky-500" />}</span>
                  <span className="min-w-0">
                    <span className={`block text-[11px] ${botMode === m ? 'font-semibold text-foreground' : 'text-foreground'}`}>{titulo}</span>
                    <span className="block text-[10px] text-muted-foreground leading-snug">{desc}</span>
                  </span>
                </button>
              ))}

              {botMode === 'campanha' && (
                <>
                  <div className="px-3 pt-2 pb-1 border-t border-border/50 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Campanha
                  </div>
                  <div className="overflow-y-auto max-h-44 scrollbar-thin">
                    {campanhas.length === 0 && (
                      <div className="px-3 py-2 text-[10px] text-muted-foreground">Nenhuma campanha nos últimos 90 dias.</div>
                    )}
                    {campanhas.map(c => (
                      <button key={c.id} onClick={() => salvarBot({ campaignId: c.id })}
                        disabled={botSaving}
                        title={`${c.contacts_total} contatos · ${c.clicados} clicaram no botão`}
                        className={`w-full text-left px-3 py-1.5 hover:bg-secondary disabled:opacity-40 ${botCfg?.campaignId === c.id ? 'bg-sky-500/10' : ''}`}>
                        <span className={`block text-[11px] truncate ${botCfg?.campaignId === c.id ? 'font-semibold text-foreground' : 'text-foreground'}`}>
                          {c.template_name}
                        </span>
                        <span className="block text-[10px] text-muted-foreground tabular-nums">
                          {Number(c.contacts_total || 0).toLocaleString('pt-BR')} contatos · {c.clicados} cliques · {new Date(c.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="px-3 py-2 border-t border-border/50 space-y-1.5">
                    {backlogJob?.rodando ? (
                      <div className="text-[10px] text-sky-600 dark:text-sky-400 tabular-nums">
                        <FiLoader className="inline w-3 h-3 animate-spin mr-1" />
                        respondendo {backlogJob.feitos}/{backlogJob.total} · {backlogJob.enviados} enviadas
                      </div>
                    ) : (
                      <>
                        <div className="text-[10px] text-muted-foreground">
                          {backlog
                            ? <><span className="font-semibold text-foreground tabular-nums">{backlog.total}</span> em aberto sem nenhuma resposta{backlog.foraJanela > 0 && <> · <span className="text-amber-600 dark:text-amber-400 tabular-nums">{backlog.foraJanela}</span> fora da janela de 24h</>}</>
                            : 'conferindo o backlog…'}
                        </div>
                        <button onClick={() => setConfirmBacklog(true)}
                          disabled={!backlog || backlog.dentroJanela === 0}
                          title={backlog?.dentroJanela ? `Responde as ${backlog.dentroJanela} que ainda estão na janela de 24h, uma a cada ~4s. Conversa que já tem resposta sua não é tocada.` : 'Nada em aberto dentro da janela de 24h'}
                          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-sky-500/40 text-sky-600 dark:text-sky-400 text-[10px] font-medium px-2 py-1 hover:bg-sky-500/10 disabled:opacity-40 disabled:cursor-not-allowed">
                          responder as {backlog?.dentroJanela ?? 0} em aberto
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}

              {botErro && (
                <div className="px-3 py-2 border-t border-border/50 text-[10px] text-red-500 flex items-start gap-1.5">
                  <FiAlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span className="flex-1">{botErro}</span>
                  <button onClick={() => setBotErro(null)} title="Fechar" className="shrink-0 hover:text-foreground"><FiX className="w-3 h-3" /></button>
                </div>
              )}
              <div className="px-3 py-2 border-t border-border/50 text-[9px] leading-snug text-muted-foreground">
                {botCfg?.dryRun && (
                  <div className="mb-1 text-amber-600 dark:text-amber-400 font-medium">
                    <FiAlertTriangle className="inline w-3 h-3 mr-0.5" />
                    modo de teste: calcula e registra no log, mas NÃO envia nada
                  </div>
                )}
                {botCfg?.taxas ? (
                  <>multa {botCfg.taxas.multaPct}% + juros {botCfg.taxas.jurosDia}%/dia · até {botCfg.maxParcelas}× · ajustar em Cadastros → Contas e Cobrança</>
                ) : (
                  <span className="text-red-500">
                    <FiAlertTriangle className="inline w-3 h-3 mr-0.5" />
                    taxas indisponíveis — o bot não vai cotar valor nenhum
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        )}

        {/* Consulta Draco — financeiro de QUALQUER cliente (em dia ou devendo),
            direto do Oracle, sem sair do Conversas */}
        <button
          onClick={() => setShowConsultaDraco(true)}
          title="Consultar o financeiro de qualquer cliente no Oracle DRACO — por código, CPF ou nome (débitos em aberto e pagamentos)"
          className={neu
            ? 'order-3 inline-flex items-center justify-center w-9 h-9 rounded-lg text-xs font-medium text-foreground transition-colors neu-convex'
            : 'inline-flex items-center gap-1.5 h-8 sm:h-9 px-2.5 rounded-lg border border-foreground/20 bg-foreground/5 text-xs font-medium text-foreground hover:bg-foreground/10 transition-colors'}
        >
          <FiDatabase className="w-3.5 h-3.5 shrink-0" />
          {!neu && <span className="hidden sm:inline">Consulta Draco</span>}
        </button>

        {/* Busca — ao lado do toggle, sem ml-auto (não empurra pro canto) */}
        <div className={`relative flex items-center${neu ? ' ml-auto order-1' : ''}`}>
          <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar…"
            title="Buscar conversa por nome ou telefone"
            className={`h-8 sm:h-9 ${neu ? 'w-56' : 'w-96'} max-w-[45vw] rounded-lg border border-foreground/20 bg-background pl-8 pr-7 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-foreground/30 transition-colors ${neu ? 'neu-inset' : ''}`}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              title="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {chamarErro && (
        <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/30 text-[11px] text-amber-700 dark:text-amber-400 flex items-center gap-2 flex-shrink-0">
          <FiAlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1">{chamarErro}</span>
          <button onClick={() => setChamarErro(null)} title="Fechar" className="hover:text-foreground"><FiX className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="flex-1 overflow-hidden relative">
        <WhatsAppInboxPanel
          key={`inbox-${deviceId}`}
          toolbarSlot={toolbarSlot}
          isOpen={true}
          onClose={() => {}}
          deviceId={deviceId}
          deviceName={deviceNome}
          deviceAiEnabled={false}
          deviceApiType="meta"
          embedded={true}
          externalSearchTerm={search}
          externalOpenConversationId={ehFaturamento ? openConversationId : null}
          onExternalOpenHandled={onOpenHandled}
          externalOpenContact={abrirContato}
          onExternalContactHandled={() => setAbrirContato(null)}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmBacklog}
        type="warning"
        title="Responder o backlog da campanha"
        message={`As mensagens saem pelo WhatsApp do Faturamento${botCfg?.campanha ? ` para quem está na lista da campanha ${botCfg.campanha.template_name}` : ''}. Não dá pra desfazer depois de enviado.`}
        details={backlogDetalhes()}
        confirmText={`Enviar para ${backlog?.dentroJanela ?? 0}`}
        cancelText="Agora não"
        onConfirm={responderBacklog}
        onCancel={() => setConfirmBacklog(false)}
      />

      {showConsultaDraco && <ConsultaDracoModal onClose={() => setShowConsultaDraco(false)} />}
    </div>
  )
}
